import React, { forwardRef, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  SelectChangeEvent,
  Slide,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import ReactQuill from 'react-quill';
import { emailTemplate, toTitleCase } from '../CandidateManagement/CandidateContent/CandidateContent';
import { TransitionProps } from '@mui/material/transitions';
import { theme } from '../../../theme';
import CloseIcon from '@mui/icons-material/Close';
import { useWarningPop } from '../../../Context/WarningPopContext';
import { validateEmail } from '../Validation/validation';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';

const Transition = forwardRef((
  props: TransitionProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>
) => {
  return <Slide direction="left" ref={ref} {...props} />;
});

interface ActionProps {
  isOpen: boolean;
  onClose: () => void;
  email: string | null;
  setEmail: (value: string) => void;
  status: string | null;
  subject: string;
  message: string;
  handleStatusChange: (event: SelectChangeEvent<string>) => void;
  setSubject: (value: string) => void;
  setMessage: (value: string) => void;
  emailData: emailTemplate[];
  onSend: () => void;
  candidateName: string | null;
  preboardingStatus: string | null;
  setStatus: (value: string | null) => void;
  candidateNum: number | null;
  onEmailSave: (payLoad: any) => Promise<any>;
  wantHiringExperience: boolean;
  setWantHiringExperience: (value: boolean) => void;
  setIsRestartPreboarding: (value: boolean) => void;
}

const Action: React.FC<ActionProps> = ({
  isOpen,
  onClose,
  email,
  status,
  subject,
  message,
  handleStatusChange,
  setSubject,
  setMessage,
  emailData,
  onSend,
  candidateName,
  preboardingStatus,
  setStatus,
  setEmail,
  candidateNum,
  onEmailSave,
  wantHiringExperience,
  setWantHiringExperience,
  setIsRestartPreboarding
}) => {

  // ...existing code...
  const filteredEmailData = emailData.filter(option => {
    const emailKey = option.EmailKey.toLowerCase();
    const status = preboardingStatus?.toLowerCase();

    // If preboardingStatus is "hired", show only "Employee Confirmation" template
    if (status === "hired") {
      return emailKey === "employee confirmation";
    }

    // Show "Start Preboarding" if preboardingStatus is null or offerpending
    if (
      emailKey === "start preboarding" &&
      (
        !status ||
        status === "offerpending"
      )
    ) {
      return true;
    }
    // Don't show "Reminder" if preboardingStatus is offerpending
    if (
      emailKey === "reminder" &&
      (!status || ["offerpending", "rejected", "offerdeclined"].includes(status))
    ) {
      return false;
    }

    // Don't show "rejected" if preboardingStatus is rejected or offerdeclined
    if (
      emailKey === "rejected" &&
      (!status || ["rejected", "offerdeclined"].includes(status))
    ) {
      return false;
    }
    // Show "reminder", "rejected" otherwise
    return [
      "reminder",
      "rejected"
    ].includes(emailKey);
  });

  // Show Restart Preboarding only for rejected or offerdeclined
  const showRestartPreboarding =
    ["rejected", "offerdeclined"].includes(preboardingStatus?.toLowerCase() || "");


  const emailDropdownOptions = showRestartPreboarding
    ? [
      ...filteredEmailData,
      {
        EmailNum: 0,
        EmailKey: "Start Preboarding", // Use the same template key
        EmailSubject: "Restart Preboarding",
      },
    ]
    : filteredEmailData;

  const [editEmail, setEditEmail] = useState("")
  const [isEditEmail, setIsEditEmail] = useState(false)
  const [isChangedEmail, setIsChangedEmail] = useState(false)
  const [emailError, setEmailError] = useState("")
  const { openWarningPop } = useWarningPop();

  useEffect(() => {
    if (isOpen) {
      setEditEmail(email || "")
      setIsChangedEmail(false)
      setIsEditEmail(false)
    }
  }, [email, isOpen,])

  useEffect(() => {
    setStatus(null);
    setMessage('');
    setSubject('');
    setWantHiringExperience(false);
  }, [isOpen])

  // ✅ Centralized handler
  const handleEmailChange = (newEmail: string) => {
    setEditEmail(newEmail);

    if (newEmail.trim() !== (email ?? "").trim()) {
      setIsChangedEmail(true);
    } else {
      setIsChangedEmail(false);
    }
    setEmailError("")
  };

  // ✅ Cancel handler → rollback to original email
  const handleUndoEmailEdit = () => {
    setEditEmail(email || "");       // reset to original
    setIsEditEmail(false);
    setIsChangedEmail(false);
    setEmailError("");
  };

  const handleConfirmSaveEmail = async () => {
    const payLoad = {
      CandidateEmail: editEmail.trim(),
      CandidateNum: candidateNum
    }
    const result = await onEmailSave(payLoad)
    if (result) {
      setEmail(editEmail.trim());
      setIsEditEmail(false);
      setIsChangedEmail(false);
    }

  };

  const handleSaveEmail = () => {
    if (editEmail.trim() === "") {
      setEmailError("Email cannot be empty.");
      return
    }
    if (!validateEmail(editEmail.trim())) {
      setEmailError("Please enter a valid email address.");
      return
    }


    if (isChangedEmail) {
      openWarningPop(() => {
        handleConfirmSaveEmail();
      }, "This will update the candidate's email throughout the system. Do you want to proceed?");
      return
    }

    setEmail(editEmail.trim());
    setIsEditEmail(false);
    setIsChangedEmail(false);

  }


  return (
    <Dialog
      open={isOpen}
      onClose={() => { }}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)",}}}
      PaperProps={{
        sx: {
          maxWidth: '22vw',
          height: '100vh',
          margin: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', p: '2vh 1.5vw 0px 1vw' }}>
        <Box
          color={theme.palette.secondary.main}
          sx={{
            border: '1px solid #EEEEEE',
            bgcolor: theme.palette.secondary.light,
            margin: '0.3vw',
            mr: '1vw',
            width: '3vw',
            height: '3vw',
            borderRadius: '1vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i className="fa-regular fa-bell"></i>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Notify</Typography>
          <Typography variant="caption">{toTitleCase(candidateName || '')}</Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          sx={{ position: 'relative', width: '2vw', height: '4vh' }}
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Communication Medium */}
        <TextField
          label="Email"
          size="small"
          value={editEmail}
          fullWidth
          margin="dense"
          required
          onChange={(e) => handleEmailChange(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          InputProps={{
            endAdornment: isEditEmail ? (
              <InputAdornment position="end">
                <IconButton onClick={handleUndoEmailEdit}>
                  <ClearIcon fontSize="small" titleAccess="Undo" />
                </IconButton>
                <IconButton onClick={handleSaveEmail}>
                  <DoneIcon fontSize="small" titleAccess="Save" />
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end" >
                <IconButton onClick={() => { setIsEditEmail(true) }}>
                  <EditIcon fontSize="small" titleAccess='Edit' />
                </IconButton>

              </InputAdornment>
            ),
            readOnly: !isEditEmail,
          }}
        />

        {/* Status Dropdown */}
        {emailDropdownOptions.length > 0 && (
          <FormControl fullWidth margin="dense" sx={{ mt: '2vh' }} required>
            <InputLabel size="small">Status</InputLabel>
            <Select
              size="small"
              label="Status"
              value={status || ''}
              onChange={(event) => {
                handleStatusChange(event);
                const selectedKey = event.target.value;
                if (showRestartPreboarding && selectedKey === "Start Preboarding") {
                  setIsRestartPreboarding(true);
                } else {
                  setIsRestartPreboarding(false);
                }
              }}
            >
              {emailDropdownOptions.map((option) => (
                <MenuItem key={option.EmailNum} value={option.EmailKey}>
                  {option.EmailSubject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Subject */}
        <TextField
          label="Subject"
          size="small"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          sx={{ marginY: '2vh' }}
          required
          autoComplete='off'
        // error={!subject}
        // helperText={!subject ? 'Subject is required' : ''}
        />

        {/* Message */}
        <ReactQuill
          value={message}
          onChange={setMessage}
          style={{ height: '28vh', marginTop: '1vh' }}
          modules={{
            toolbar: [['bold', 'italic', 'link', { list: 'ordered' }, { list: 'bullet' }]],
          }}
          readOnly={!status}
        />

        <Box mt={6}>
          {
            status?.toLowerCase() === "employee confirmation" && (
              <Tooltip title="Enable this if you want to collect hiring experience feedback from the candidate." slotProps={{
                popper: {
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "#fff",
                      color: "#000",
                      fontSize: "0.75rem",
                      border: "1px solid #ccc",
                    },
                    maxWidth: '17vw'
                  },
                },
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={wantHiringExperience}
                      onChange={(_, checked) => setWantHiringExperience(checked)}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Collect hiring feedback</Typography>}
                />
              </Tooltip>
            )
          }
        </Box>

      </DialogContent>
      <DialogActions >
        <Button onClick={onSend} variant="contained" color="primary" fullWidth
          disabled={isEditEmail}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Action;